import React, { useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { CalendarDaysIcon, ChefHatIcon, CompassIcon, HeartIcon, LifeBuoyIcon, MenuIcon, MessageSquareIcon, SettingsIcon, StarIcon, UserIcon, WalletIcon, XIcon, LayoutDashboardIcon, CalendarClockIcon } from 'lucide-react';
import { Logo } from './Logo';
import { PrefsMenu } from './PrefsMenu';
import { Avatar } from '../ui/Avatar';
import { useSession } from '../../contexts/SessionContext';
import { useAppData } from '../../contexts/AppDataContext';
import { spring } from '../../utils/motion';
import { cn } from '../../utils/cn';
interface NavItem {
  to: string;
  label: string;
  icon: React.ReactNode;
  badge?: number;
  end?: boolean;
}
export function AppShell({
  title,
  subtitle,
  actions,
  children,
  wide = false,
  flush = false
}: {
  title: string;
  subtitle?: string;
  actions?: React.ReactNode;
  children: React.ReactNode;
  wide?: boolean;
  flush?: boolean;
}) {
  const {
    viewer,
    customer,
    chef
  } = useSession();
  const {
    threads
  } = useAppData();
  const {
    pathname
  } = useLocation();
  const [open, setOpen] = useState(false);
  const reduce = useReducedMotion();
  /** The route decides which side of the marketplace the nav belongs to. */
  const isChef = pathname.startsWith('/chef') || viewer === 'chef' && !pathname.startsWith('/app/account');
  const unread = threads.reduce((sum, t) => sum + (isChef ? t.chefId === chef.id ? t.unreadForChef : 0 : t.customerId === customer.id ? t.unreadForCustomer : 0), 0);
  const primary: NavItem[] = isChef ? [{
    to: '/chef',
    label: 'Dashboard',
    icon: <LayoutDashboardIcon className="h-4 w-4" data-id="element-781" data-mp="1np8zm4e9hthp" />,
    end: true
  }, {
    to: '/chef/inbox',
    label: 'Inbox',
    icon: <MessageSquareIcon className="h-4 w-4" data-id="element-782" data-mp="3dzahk5kmdfnw" />,
    badge: unread
  }, {
    to: '/chef/bookings',
    label: 'Bookings',
    icon: <CalendarDaysIcon className="h-4 w-4" data-id="element-783" data-mp="3i0nmmdfw1etr" />
  }, {
    to: '/chef/studio/menus',
    label: 'Menus & rates',
    icon: <ChefHatIcon className="h-4 w-4" data-id="element-784" data-mp="pvg4btev3835" />
  }, {
    to: '/chef/studio/availability',
    label: 'Availability',
    icon: <CalendarClockIcon className="h-4 w-4" data-id="element-785" data-mp="ltcnep9fbbah" />
  }, {
    to: '/chef/earnings',
    label: 'Earnings',
    icon: <WalletIcon className="h-4 w-4" data-id="element-786" data-mp="gvb4c4bzzbz0" />
  }, {
    to: '/chef/reviews',
    label: 'Reviews',
    icon: <StarIcon className="h-4 w-4" data-id="element-787" data-mp="1dksil7p1q6cb" />
  }] : [{
    to: '/discover',
    label: 'Find a chef',
    icon: <CompassIcon className="h-4 w-4" data-id="element-788" data-mp="19ix8bdp50ime" />
  }, {
    to: '/app/inbox',
    label: 'Inbox',
    icon: <MessageSquareIcon className="h-4 w-4" data-id="element-789" data-mp="14sguvtlbgx01" />,
    badge: unread
  }, {
    to: '/app/bookings',
    label: 'Bookings',
    icon: <CalendarDaysIcon className="h-4 w-4" data-id="element-790" data-mp="zu5ltl2uiqiv" />
  }, {
    to: '/app/saved',
    label: 'Saved chefs',
    icon: <HeartIcon className="h-4 w-4" data-id="element-791" data-mp="1x9jhx9esiayi" />
  }, {
    to: '/app/account/profile',
    label: 'Account',
    icon: <UserIcon className="h-4 w-4" data-id="element-792" data-mp="1shrmakgs1f1i" />
  }];
  const secondary: NavItem[] = isChef ? [{
    to: '/chef/studio/profile',
    label: 'Profile & docs',
    icon: <UserIcon className="h-4 w-4" data-id="element-793" data-mp="1zvk8czubq1l9" />
  }, {
    to: '/chef/support',
    label: 'Support',
    icon: <LifeBuoyIcon className="h-4 w-4" data-id="element-794" data-mp="24ssmmpccplh8" />
  }, {
    to: '/chef/settings',
    label: 'Settings',
    icon: <SettingsIcon className="h-4 w-4" data-id="element-795" data-mp="29qc3zy4ciede" />
  }] : [{
    to: '/app/support',
    label: 'Support',
    icon: <LifeBuoyIcon className="h-4 w-4" data-id="element-796" data-mp="2dr4e0tl1knaq" />
  }, {
    to: '/app/settings',
    label: 'Settings',
    icon: <SettingsIcon className="h-4 w-4" data-id="element-797" data-mp="2iq7jug8uixjr" />
  }];
  const person = isChef ? {
    name: chef.name,
    photo: chef.photo,
    role: 'Chef account'
  } : {
    name: customer.name,
    photo: customer.photo,
    role: 'Guest account'
  };
  const navList = (items: NavItem[], onNavigate?: () => void) => <ul className="space-y-0.5" data-id="element-798" data-mp="2xgok3w9qvhf2">
      {items.map(item => <li key={item.to} data-id="element-799" data-mp="31jbdi1m2daed">
          <NavLink to={item.to} end={item.end} onClick={onNavigate} className={({
        isActive
      }) => cn('flex items-center gap-2.5 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors duration-150 ease-out', isActive ? 'bg-primary-soft text-primary' : 'text-ink-2 hover:bg-oat hover:text-ink')} data-id="element-800" data-mp="31k4hiutn1lst">
            <span aria-hidden="true" data-id="element-801" data-mp="32mtdq7j13fdf">{item.icon}</span>
            <span className="flex-1" data-id="element-802" data-mp="2xn71a0b0y35o">{item.label}</span>
            {!!item.badge && <span className="tnum rounded-full bg-action px-1.5 py-0.5 text-[11px] font-semibold text-on-action" data-id="element-803" data-mp="29xcplm2fykn0">{item.badge}</span>}
          </NavLink>
        </li>)}
    </ul>;
  return <div className="flex min-h-screen w-full bg-canvas" data-id="element-804" data-mp="2jvae8ao7qjg5">
      <aside className="sticky top-0 hidden h-screen w-60 shrink-0 flex-col border-r border-hairline bg-surface px-4 py-5 lg:flex" data-id="element-805" data-mp="2evo0m04h316t">
        <Logo to={isChef ? '/chef' : '/app/bookings'} className="px-2" data-id="element-806" data-mp="3v8l9n35zwoe5" />
        <nav aria-label="Primary" className="mt-7 flex-1" data-id="element-807" data-mp="3qb2vmyd4fiza">
          {navList(primary)}
          <div className="my-4 border-t border-hairline" data-id="element-808" data-mp="1c31zmiw11t2n" />
          {navList(secondary)}
        </nav>
        <Link to={isChef ? '/chef/studio/profile' : '/app/account/profile'} className="flex items-center gap-3 rounded-lg px-2 py-2 transition-colors duration-150 ease-out hover:bg-oat" data-id="element-809" data-mp="1h1n2kq2nyqus">
          <Avatar name={person.name} src={person.photo} size="sm" data-id="element-810" data-mp="180f3w6w8jo06" />
          <span className="min-w-0" data-id="element-811" data-mp="1vqrke65jqt2d">
            <span className="block truncate text-sm font-medium text-ink" data-id="element-812" data-mp="1vru9q9n80ng6">{person.name}</span>
            <span className="block text-[12px] text-ink-3" data-id="element-813" data-mp="1zsmlpanh96g5">{person.role}</span>
          </span>
        </Link>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col" data-id="element-814" data-mp="1m06l0gi1tkie">
        <header className="sticky top-0 z-30 border-b border-hairline bg-canvas/92 backdrop-blur-md" data-id="element-815" data-mp="1qzt3b4cqhqy2">
          <div className={cn('mx-auto flex items-center gap-3 px-5 py-3.5 lg:px-8', wide ? 'max-w-none' : 'max-w-shell')} data-id="element-816" data-mp="2epx1uxb2f0i7">
            <button type="button" onClick={() => setOpen(true)} aria-label="Open menu" className="rounded-lg border border-hairline p-2 text-ink-2 lg:hidden" data-id="element-817" data-mp="2jhf55s0k9d6c">
              <MenuIcon className="h-5 w-5" aria-hidden="true" data-id="element-818" data-mp="2oe0zywebwnyk" />
            </button>
            <div className="min-w-0 flex-1" data-id="element-819" data-mp="2jcvad7fytgt9">
              <h1 className="truncate font-display text-lg font-semibold tracking-[-0.01em] text-ink sm:text-xl" data-id="element-820" data-mp="2ef3fc4k0xeku">{title}</h1>
              {subtitle && <p className="truncate text-[13px] text-ink-2" data-id="element-821" data-mp="2aejhszvjg8y6">{subtitle}</p>}
            </div>
            <div className="flex items-center gap-2" data-id="element-822" data-mp="25mi213z5w03e">
              {actions}
              <PrefsMenu compact showLocale={false} data-id="element-823" data-mp="20nf2fz1yam2t" />
            </div>
          </div>
        </header>

        <main className={cn('flex-1', flush ? '' : 'px-5 py-6 lg:px-8 lg:py-8')} data-id="element-824" data-mp="1vp3uxuz4onnr">
          <div className={cn(wide || flush ? '' : 'mx-auto max-w-shell')} data-id="element-825" data-mp="1qphfdevtuvbs">{children}</div>
        </main>
      </div>

      <AnimatePresence data-id="element-826" data-mp="1mndtrxfg2acs">
        {open && <div className="fixed inset-0 z-50 flex lg:hidden" role="dialog" aria-modal="true" aria-label="Menu" data-id="element-827" data-mp="1mop7oq2lsyhm">
            <motion.button type="button" aria-label="Close menu" onClick={() => setOpen(false)} initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.2,
          ease: 'easeOut'
        }} className="absolute inset-0 bg-ink/40" data-id="element-828" data-mp="27xt3qtejv1v" />
            <motion.div initial={reduce ? {
          opacity: 0
        } : {
          x: -24,
          opacity: 0
        }} animate={reduce ? {
          opacity: 1
        } : {
          x: 0,
          opacity: 1
        }} exit={reduce ? {
          opacity: 0
        } : {
          x: -24,
          opacity: 0
        }} transition={reduce ? {
          duration: 0.15
        } : spring.smooth} className="relative z-10 flex h-full w-72 flex-col border-r border-hairline bg-surface px-4 py-5" data-id="element-829" data-mp="3eoursyt97enb">
              <div className="flex items-center justify-between" data-id="element-830" data-mp="30vvaqsdtpcdn">
                <Logo to={isChef ? '/chef' : '/app/bookings'} className="px-2" data-id="element-831" data-mp="30u0qlgadloam" />
                <button type="button" onClick={() => setOpen(false)} aria-label="Close menu" className="rounded-lg p-1.5 text-ink-3 hover:bg-oat hover:text-ink" data-id="element-832" data-mp="1u9c6v79yw0w2">
                  <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-833" data-mp="1ya4gi1mr47sv" />
                </button>
              </div>
              <nav aria-label="Primary" className="mt-6 flex-1 overflow-y-auto" data-id="element-834" data-mp="2d75a6aoi6ctz">
                {navList(primary, () => setOpen(false))}
                <div className="my-4 border-t border-hairline" data-id="element-835" data-mp="287iw5z0uosk4" />
                {navList(secondary, () => setOpen(false))}
              </nav>
              <Link to="/" onClick={() => setOpen(false)} className="rounded-lg px-3 py-2.5 text-sm text-ink-2 hover:bg-oat hover:text-ink" data-id="element-836" data-mp="2hy3rndl89gyt">
                Back to panandapron.com
              </Link>
            </motion.div>
          </div>}
      </AnimatePresence>
    </div>;
}